import { Box, Dialog, DialogContent, Fab, Grid, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';

import {
    ArrowBack,
    ArrowForward,
    AssignmentOutlined as AddRoomIcon,
    CalendarToday as DateIcon,
    MeetingRoomOutlined as RoomIcon,
    PeopleOutline as InstructorIcon,
    PersonAddOutlined as AddInstructorIcon,
    FileDownload as ExportIcon,
} from '@mui/icons-material';
import { filterBy } from '@progress/kendo-data-query';
import { ExcelExport as KendoExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import * as Sentry from '@sentry/react';
import { produce } from 'immer';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';
import BattleshipApi from '../../api/battleship.api';
import * as theme from '../../theme';

//models
import BattleshipObjectGroupDto from '../../models/battleship/battleshipObjectGroup';
import TimetableFunction from '../../models/timetable/timetableFunction';
import TimetableSlot from '../../models/timetable/timetableSlot';
import BattleShipMove from '../../models/battleship/battleshipMove';
import { battleshipMoveIds, timetableContainerIds, timetableEntryIds } from '../../helpers/sentry';

//icons
import { ReactComponent as ArrowIcon } from '../../assets/images/arrow.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/add-icon.svg';
import { ReactComponent as ViewIcon } from '../../assets/icons/view-icon.svg';
import { ReactComponent as ChangeIcon } from '../../assets/icons/change-icon.svg';

import { MOMENT_DATE_FORMAT, MOMENT_TIME_DISPLAY_FORMAT } from '../../constants/date_format';
import defaultState from '../../models/battleship/defaultState';
import { ExcelExport } from '../../models/battleship/excelExport';
import { deleteLocalStorage, getLocalStorage, setLocalStorage } from '../../utils/storage';

// import app components and functions
import Timetable from './timetable';
import Spacer from '../../components/global/spacer';
import Button from '../../components/battleship/customButton';
import CustomDropdown from '../../components/battleship/customDropdown';
import TimetableTabs from '../../components/battleship/timetableTabs';
import DepartmentsApi from '../../api/departments.api';
import TimetableApi from '../../api/timetable/timetable.api';
import { setTermSchool } from '../../store/reducers/app';
import { useAppDispatch } from '../../hooks/hooks';
import { accountTypes, CENTRAL_TIMETABLER_VIEW_TYPE } from '../../constants';

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface RightTimetableContainerProps {
    type: number;
    isView: boolean;
}

interface SidebarInfoProps {
    type?: number;
    active?: boolean;
    error?: boolean;
}

interface SidebarItemButtonProps {
    active?: boolean;
}

interface TimeInfoProps {
    capitalize?: boolean;
}

const Battleship = (props) => {
    let _exporter;
    const { schoolID, termSchoolID, firstName, lastName, termCode, termStartDate, termEndDate, termSchools, accountType, viewType } = props;

    const dispatch = useAppDispatch();

    const filteredTermSchools = termSchools.filter((item) => item.schoolId === schoolID);

    const LOCAL_STORAGE_KEY = `Timetabling_Moves_${firstName}_${lastName}_${termSchoolID}`;

    const _api = new BattleshipApi();
    const _departmentApi = new DepartmentsApi();
    const _timetableApi = new TimetableApi();

    const [data, setData] = useState({ ...defaultState });
    const [type, setType] = useState(0); // 0 = set, 1 = instructor, 2 = room, 3 = set -> instructor, 4 -> set to room
    const [sidebar, setSidebar] = useState(false);
    const [week, setWeek] = useState(termCode?.endsWith('B') ? 1 : 2); // Default week: use 2 except for B-Terms
    const [term, setTerm] = useState();
    const [deleteConfirm, setDeleteConfirm] = useState({ active: false, index: null });
    const [confirmDeleteLocal, setConfirmDeleteLocal] = useState(false);

    const [excelExportData, setExcelExportData] = useState([]);
    const [instructorToFilter, setInstructorToFilter] = useState('');
    const [leftIndex, setLeftIndex] = useState(0);
    const [rightIndex, setRightIndex] = useState(0);

    const [showAllInstructors, setShowAllInstructors] = useState(false);

    const [rightSideTabTimetables, setRightSideTabTimetables] = useState([]);

    const isCentralTimetablerView = accountType === accountTypes.centralTimetabler || viewType === CENTRAL_TIMETABLER_VIEW_TYPE;


    useEffect(() => {
        const loadData = async () => {
            const departments = await _departmentApi.GetDepartments(isCentralTimetablerView ? null : schoolID, null);
            const buildings = await _api.GetBuildings(isCentralTimetablerView ? null : schoolID);
            const instructors = await _api.GetBattleshipInstructors(isCentralTimetablerView ? null : schoolID, null, null);
            const subjects = await _api.GetSubjects();
            const localStorageData = getLocalStorage(LOCAL_STORAGE_KEY);

            setData((prevState) => ({
                ...prevState,
                departments,
                subjects,
                allDepartments: departments,
                buildings,
                allBuildings: buildings,
                instructors: instructors,
                instructorsTo: instructors,
                allInstructors: instructors,
                buildingsTo: buildings,
                isLoading: false,
            }));

            if (localStorageData && localStorageData.timetableMoves.length) {
                setConfirmDeleteLocal(true);
            }
        };

        schoolID && termSchoolID && loadData();
    }, [schoolID, termSchoolID]);

    useEffect(() => {
        const customId = 'custom-id-yes';

        if (week <= 0) {
            toast.info('Week cannot be less than 0', { toastId: customId });
            return;
        }
        const momentStartDate = moment(termStartDate, MOMENT_DATE_FORMAT);
        const momentEndDate = moment(termEndDate, MOMENT_DATE_FORMAT);

        const totalWeeks = momentEndDate.diff(momentStartDate, 'week');

        if (week > totalWeeks) {
            toast.info('Cannot exceed term end date', { toastId: customId });
            return;
        }

        setWeek(week);

        if ((type === 0 || type === 3) && data.setsSelected.length) {
            onSetsChange({ target: { value: data.setsSelected } });
        }

        if (type === 4 && data.setsSelected.length) {
            onSetChange({ target: { value: data.setsSelected[0] } });
        }

        if (type === 1 && data.instructorsSelectedFrom.length) {
            handleInstructorChange(data.instructorsSelectedFrom[0], 'from');
        }

        if (type === 2 && data.roomsSelectedFrom.length) {
            onRoomChange(data.roomsSelectedFrom[0], 'from');
        }
    }, [week]);

    /*
     * For Instructor to Instructor swap and SET to INSTRUCTOR, timetableTo is updated with the moved course automatically
     */
    useEffect(() => {
        if (data.timetableSelectedItem && (type === 1 || type === 3) && data.instructorsSelectedTo[0] && data.changeIndex === data.timetableMoves.length) {
            handleInstructorChange(data.instructorsSelectedTo[0], 'to');
        }
    }, [data.timetableSelectedItem]);

    const GetUpdatedTimetables = async (termSchoolId: number, objectTypeId: number, objectId: number, timetableMoves: Array<BattleShipMove>, week: number) => {
        try {
            return await _api.GetUpdatedTimetables(termSchoolID, objectTypeId, objectId, week, timetableMoves);
        } catch (error) {
            Sentry.captureException(error, (scope) => {
                scope.setContext('data', {
                    termSchoolId,
                    objectTypeId,
                    objectId,
                    timetableMoves: battleshipMoveIds(timetableMoves),
                    week,
                });

                return scope;
            });
        }
    };

    const GetUpdatedTimetable = async (termSchoolId: number, objectTypeId: number, objectId: number, timetableMoves: Array<BattleShipMove>, week: number) => {
        try {
            const timetables = await _api.GetUpdatedTimetables(termSchoolID, objectTypeId, objectId, week, timetableMoves);
            const timetableIndex = timetables.findIndex((t) => t.objectID === objectId && t.objectTypeID === objectTypeId);

            if (timetableIndex === -1) {
                toast.info('No data exists for selected timetable');
                return null;
            }
            return timetables[timetableIndex];
        } catch (error) {
            Sentry.captureException(error, (scope) => {
                scope.setContext('data', {
                    termSchoolId,
                    objectTypeId,
                    objectId,
                    timetableMoves: battleshipMoveIds(timetableMoves),
                    week,
                });

                return scope;
            });
        }
    };

    const handleTermChange = (term: any) => {
        setTerm(term);
        setData({ ...defaultState });
        dispatch(setTermSchool(term));
    };

    const onDepartmentChange = async (item, addAllLevelSetSelection: boolean = false): Promise<void> => {
        const department = item;

        // TODO: uncomment for multiSelect
        // const departmentSelected = [...data.departmentSelected];

        // if (departmentSelected.indexOf(department) !== -1) return;
        // else departmentSelected.push(department);

        let sets = await _timetableApi.GetSets(department.departmentID);

        if (addAllLevelSetSelection && sets && sets.length) {
            const setLevels = Array.from(new Set(sets.map((s: any) => s.level))).map((l) => {
                return { isLevel: true, setID: l, setCode: `All Level ${l}` };
            });

            sets = [...setLevels, ...sets];
        }

        setData({
            ...data,
            // departmentSelected,
            departmentSelected: [department],
            sets,
            allSets: sets,

            setsSelected: [],
            setTimetables: [],

            timetableFrom: null,
            timetableTo: null,

            timetableSelectedItem: null,
            timetablePendingChange: null,

            instructorsSelectedTo: [],
            buildingSelectedTo: [],
            roomsSelectedTo: [],

            instructorTimetables: [],
        });
    };
    // TODO: test the remove function with sets associated to each dept
    const onRemoveDepartment = (item) => {
        const departmentSelected = [...data.departmentSelected];

        const index = departmentSelected.indexOf(item);
        if (index > -1) {
            departmentSelected.splice(index, 1);
        }

        setData({
            ...data,
            departmentSelected,
        });
    };

    const onSetsChange = async (item) => {
        // TODO: generate all sets for all programs selected
        let sets = [...data.setsSelected];

        sets.push(item);

        let timetableTo = data.timetableTo;
        let timetableFrom = data.timetableFrom;

        const setsAdded = sets.filter((s) => !data.setsSelected.includes(s));
        const setsDeleted = data.setsSelected.filter((s) => !sets.includes(s));

        let setTimetables = [...data.setTimetables];

        if (setsAdded.length) {
            const set = setsAdded[0];

            if (set.isLevel) {
                const selectedSetIds = sets.filter((s) => !s.isLevel).map((s) => s.setID);

                const setsToAdd = data.allSets.filter((s) => s.level === set.setID).filter((s) => !selectedSetIds.includes(s.setID));

                sets = [...sets, ...setsToAdd];

                const battleshipObjectGroupList = setsToAdd.map((s) => new BattleshipObjectGroupDto(s.setID, 1));

                try {
                    const timetables = await _api.GetGroupTimetables(battleshipObjectGroupList, data.timetableMoves, termSchoolID, week);
                    if (!timetables.length) {
                        toast.info('No timetables exist for selected sets');
                    } else {
                        setTimetables = [...setTimetables, ...timetables];
                    }
                } catch (error) {
                    Sentry.captureException(error, (scope) => {
                        scope.setContext('data', {
                            battleshipObjectGroupList,
                            termSchoolID,
                            week,
                        });
                        return scope;
                    });
                }
            } else {
                const timetable = await GetUpdatedTimetable(termSchoolID, 1, set.setID, data.timetableMoves, week);
                if (timetable) setTimetables = [...setTimetables, timetable];
            }
        }

        if (setsDeleted.length) {
            const setsDeletedIds = setsDeleted.map((s) => s.setID);
            setTimetables = setTimetables.filter((s) => !setsDeletedIds.includes(s.objectID));

            if (timetableFrom && setsDeletedIds.includes(timetableFrom.objectID)) {
                timetableFrom = null;
                timetableTo = null;
            }
        }

        sets = sets.filter((s) => !s.isLevel);

        if (type === 0 || type === 3) {
            // if no set timetable has been selected, then select the first on in the array
            setData({
                ...data,
                setTimetables: setTimetables,
                setsSelected: sets,
                timetableFrom: !data.timetableFrom ? setTimetables[0] : data.timetableFrom,
                timetableTo,
                timetableSelectedItem: null,
                timetablePendingChange: null,

                instructorTimetables: [],
            });
        }
    };

    const onSetChange = async (item) => {
        const set = item;

        const timetable = await GetUpdatedTimetable(termSchoolID, 1, set.setID, data.timetableMoves, week);

        if (type === 0) {
            setData({
                ...data,
                setsSelected: [set],
                timetableFrom: timetable,
                timetableTo: timetable,
                timetableSelectedItem: null,
                timetablePendingChange: null,
            });
        } else if (type === 3) {
            setData({
                ...data,
                setsSelected: [set],
                timetableFrom: timetable,
                timetableTo: null,
                timetableSelectedItem: null,
                timetablePendingChange: null,
                instructorsSelectedTo: [],
            });
        } else if (type === 4) {
            setData({
                ...data,
                setsSelected: [set],
                timetableFrom: timetable,
                timetableTo: null,
                timetableSelectedItem: null,
                timetablePendingChange: null,
                roomsSelectedTo: [],
                buildingSelectedTo: [],
            });
        }
    };

    const handleInstructorChange = async (instructor, fromOrTo) => {
        const allChanges = [...data.timetableMoves];

        let newChange = null;

        if (type === 1) {
            // For the second timetable we want to load the instructor timetable change right away (no clicking or selecting timeslots)
            if (fromOrTo === 'to') {
                const {
                    timetableSelectedItem: { objectID, objectTypeID, id, dayOfWeek, timeIn, instructors, room, timetableFunctions },
                } = data;

                const weekDayId = weekdays.indexOf(dayOfWeek) + 1;

                //swap out the instructor
                const instructorIds = instructors.filter((i) => i.instructorID !== objectID).map((i) => i.instructorID);
                let timetableFunctionsArr = [...timetableFunctions];
                timetableFunctionsArr = timetableFunctionsArr.map((f) => (f.instructorId === objectID ? new TimetableFunction(instructor.instructorID, f.functionId, f.setId, f.sectionId) : f));

                instructorIds.push(instructor.instructorID);

                let roomId = null;

                if (room) roomId = room.roomID;

                newChange = new BattleShipMove(
                    type,
                    data.timetableMoves.length,
                    objectID,
                    objectTypeID,
                    timeIn,
                    weekDayId,
                    renderLabel(data.timetableSelectedItem, dayOfWeek, timeIn, instructor, null),
                    id,
                    instructorIds,
                    roomId,
                    null,
                    null,
                    null,
                    instructor.instructorID,
                    timetableFunctionsArr,
                );

                allChanges.push(newChange);
            }

            const timetable = await GetUpdatedTimetable(termSchoolID, 2, instructor.instructorID, allChanges, week);

            const newData = { ...data };

            if (fromOrTo === 'from') {
                newData['instructorsSelectedFrom'] = [instructor];
                newData['timetableFrom'] = timetable;
                newData['instructorsSelectedTo'] = [];
                newData['timetableTo'] = null;
                newData['timetableSelectedItem'] = null;
                newData['timetablePendingChange'] = null;
            } else if (fromOrTo === 'to') {
                newData['instructorsSelectedTo'] = [instructor];
                newData['timetableTo'] = timetable;
                newData['timetablePendingChange'] = newChange;
            }

            setData(newData);
        } else if (type === 3) {
            const {
                timetableSelectedItem: {
                    id,
                    dayOfWeek,
                    timeIn,
                    objectTypeID,
                    objectID,
                    course: { courseID: fromCourseObjectID },
                    room,
                    instructors,
                    timetableFunctions,
                },
            } = data;

            if (instructors && instructors.length > 1) {
                toast.warn('Select a lab / lecture with only 0 or 1 instructor(s)');
                return;
            }

            let timetableFunctionsArr = [...timetableFunctions];

            //replace the existing instructor with the newly selected instructor
            timetableFunctionsArr = timetableFunctionsArr.map((f) => {
                if (!f.instructorId) return new TimetableFunction(instructor.instructorID, f.functionId, f.setId, f.sectionId);

                return f.instructorId === instructors[0].instructorID ? new TimetableFunction(instructor.instructorID, f.functionId, f.setId, f.sectionId) : f;
            });

            const weekDayId = weekdays.indexOf(dayOfWeek) + 1;

            const instructorIds = [instructor.instructorID];
            let roomId = null;
            if (room) roomId = room.roomID;

            newChange = new BattleShipMove(
                type,
                data.timetableMoves.length,
                objectID,
                objectTypeID,
                timeIn,
                weekDayId,
                renderLabel(data.timetableSelectedItem, dayOfWeek, timeIn, instructor, null),
                id,
                instructorIds,
                roomId,
                null,
                null,
                null,
                instructor.instructorID,
                timetableFunctionsArr,
            );

            allChanges.push(newChange);

            const timetable = await GetUpdatedTimetable(termSchoolID, 2, instructor.instructorID, allChanges, week);

            const newData = { ...data };

            newData['timetableTo'] = timetable;
            newData['instructorsSelectedTo'] = [instructor];
            newData['timetablePendingChange'] = newChange;

            setData(newData);
        }
    };

    const handleNextInstructorClick = async () => {
        let selectedToInstructorIndex = data.instructorsTo.findIndex((i) => i.instructorID === data.instructorsSelectedTo[0]?.instructorID);

        if (selectedToInstructorIndex === -1 || selectedToInstructorIndex >= data.instructorsTo.length - 1) {
            selectedToInstructorIndex = 0;
        } else {
            selectedToInstructorIndex++;
        }

        const nextInstructor = data.instructorsTo[selectedToInstructorIndex];

        await handleInstructorChange(nextInstructor, 'to');
    };

    const handlePreviousInstructorClick = async () => {
        let selectedToInstructorIndex = data.instructorsTo.findIndex((i) => i.instructorID === data.instructorsSelectedTo[0]?.instructorID);

        if (selectedToInstructorIndex === -1) {
            selectedToInstructorIndex = 0;
        } else if (selectedToInstructorIndex === 0) {
            selectedToInstructorIndex = data.instructorsTo.length - 1;
        } else {
            selectedToInstructorIndex--;
        }

        const previousInstructor = data.instructorsTo[selectedToInstructorIndex];

        await handleInstructorChange(previousInstructor, 'to');
    };

    const handleBuildingChange = async (building, type) => {
        const rooms = await _api.GetBuildingRooms(building.buildingID);

        if (type === 'from') {
            setData({
                ...data,
                buildingSelectedFrom: [building],
                rooms: rooms,
                roomsSelectedFrom: [],
                buildingSelectedTo: [],
                roomsSelectedTo: [],
                timetableFrom: null,
                timetableTo: null,
            });
        }

        if (type === 'to') {
            setData({
                ...data,
                buildingSelectedTo: [building],
                roomsTo: rooms,
                roomsSelectedTo: [],
            });
        }
    };

    const onRoomChange = async (room, fromOrTo) => {
        const allChanges = [...data.timetableMoves];

        let newChange = null;

        if (type === 2) {
            if (fromOrTo === 'to') {
                const {
                    timetableSelectedItem: {
                        objectID,
                        objectTypeID,
                        id,
                        dayOfWeek,
                        timeIn,
                        room: { roomID: fromRoomObjectID },
                        instructors,
                        timetableFunctions,
                    },
                } = data;

                const weekDayId = weekdays.indexOf(dayOfWeek) + 1;

                const instructorIds = instructors.map((i) => i.instructorID);

                newChange = new BattleShipMove(
                    type,
                    data.timetableMoves.length,
                    objectID,
                    objectTypeID,
                    timeIn,
                    weekDayId,
                    renderLabel(data.timetableSelectedItem, dayOfWeek, timeIn, null, room),
                    id,
                    instructorIds,
                    room.roomID,
                    data.buildingSelectedFrom[0].buildingID,
                    data.buildingSelectedTo[0].buildingID,
                    null,
                    null,
                    timetableFunctions,
                );

                allChanges.push(newChange);
            }

            const timetable = await GetUpdatedTimetable(termSchoolID, 3, room.roomID, allChanges, week);

            const newData = { ...data };

            if (fromOrTo === 'from') {
                newData['roomsSelectedFrom'] = [room];
                newData['timetableFrom'] = timetable;
                newData['roomsSelectedTo'] = [];
                newData['timetableTo'] = null;
                newData['timetableSelectedItem'] = null;
                newData['timetablePendingChange'] = null;
                newData['buildings'] = [...data.allBuildings];
                newData['buildingSelectedTo'] = [];
            } else if (fromOrTo === 'to') {
                newData['roomsSelectedTo'] = [room];
                newData['timetableTo'] = timetable;
                newData['timetablePendingChange'] = newChange;
            }

            setData(newData);
        } else if (type === 4) {
            const {
                timetableSelectedItem: {
                    id,
                    dayOfWeek,
                    timeIn,
                    objectTypeID,
                    objectID,
                    course: { courseID: fromCourseObjectID },
                    instructors,
                    timetableFunctions,
                },
            } = data;

            const weekDayId = weekdays.indexOf(dayOfWeek) + 1;
            const instructorIds = instructors.map((i) => i.instructorID);

            newChange = new BattleShipMove(
                type,
                data.timetableMoves.length,
                objectID,
                objectTypeID,
                timeIn,
                weekDayId,
                renderLabel(data.timetableSelectedItem, dayOfWeek, timeIn, null, room),
                id,
                instructorIds,
                room.roomID,
                null,
                data.buildingSelectedTo[0].buildingID,
                fromCourseObjectID,
                null,
                timetableFunctions,
            );

            allChanges.push(newChange);

            const timetable = await GetUpdatedTimetable(termSchoolID, 3, room.roomID, allChanges, week);

            const newData = { ...data };

            newData['roomsSelectedTo'] = [room];
            newData['timetableTo'] = timetable;
            newData['timetablePendingChange'] = newChange;

            setData(newData);
        }
    };

    const handleDropdownFiltering = (event, filterObjectName, dataObjectName) => {
        setData({ ...data, [dataObjectName]: filterBy(data[filterObjectName], event.filter) });

        if (dataObjectName === 'instructorsTo') setInstructorToFilter(event.filter.value);
    };

    //selecting a timeslot to move from a timetable on the left side
    const handleSelectTimetableItem = async (item) => {
        if (!item) return;

        let setInstructors = [];
        const instructorTimetables = [];
        const rightSideTabTimetables = [];
        let emptySlots: Array<TimetableSlot> = [];

        if (type === 0) {
            let instructors = [];
            let sets = [];

            //load all the instructor timetables as a group
            if (item.instructors) instructors = item.instructors;
            if (item.sets) sets = item.sets;

            const battleshipObjectGroupInstructorList = instructors.map((i) => new BattleshipObjectGroupDto(i.instructorID, 2));
            const battleshipObjectGroupSetList = sets.map((s) => new BattleshipObjectGroupDto(s.setID, 1));

            //keep the selected tab (timetable) as the first (timetable) tab on the right side as well
            const battleshipObjectGroupList = [...battleshipObjectGroupSetList, ...battleshipObjectGroupInstructorList].sort((a, b) => {
                if (a.objectId == data.timetableFrom.objectID) return 1;
                else return 0;
            });

            const { timetableMoves } = data;

            try {
                const timetables = await _api.GetGroupTimetables(battleshipObjectGroupList, data.timetableMoves, termSchoolID, week);
                emptySlots = await _api.GetOpenTimetableSlots(item, timetableMoves, termSchoolID, week, schoolID);

                rightSideTabTimetables.push(...timetables);

                //TODO: remove
                // instructorTimetables.push(...timetables);

                // rightSideTabTimetables.push(data.timetableFrom);
            } catch (error) {
                Sentry.captureException(error, (scope) => {
                    scope.setContext('data', {
                        battleshipObjectGroupList,
                        termSchoolID,
                        week,
                        timetableMoves: battleshipMoveIds(timetableMoves),
                        item: timetableEntryIds(item),
                    });
                    return scope;
                });
            }
        }

        if (type === 1 || type === 3) {
            setInstructors = await _api.GetBattleshipInstructors(null, termSchoolID, item.sets[0].setID);
        }

        if (type === 1) {
            setData({
                ...data,
                instructorsTo: data.allInstructors,
                timetableSelectedItem: item,
                timetablePendingChange: null,
                setInstructors,
            });

            return;
        }

        if (type === 2) {
            setData({
                ...data,
                timetableSelectedItem: item,
                timetableTo: null,
                timetablePendingChange: null,
                buildingSelectedTo: [],
                roomsSelectedTo: [],
            });

            return;
        }

        //reset the filter input in the instructor dropdown since new dataset is being loaded in
        if (type === 3) {
            setData({
                ...data,
                instructorsTo: data.allInstructors,
                timetableSelectedItem: item,
                timetablePendingChange: null,
                setInstructors,
            });

            return;
        }

        if (type === 4) {
            // Reset second timetable if the course changes for set to room
            setData({
                ...data,
                buildingSelectedTo: [],
                roomsSelectedTo: [],
                timetableSelectedItem: item,
                timetableTo: null,
                timetablePendingChange: null,
            });

            return;
        }

        setData({
            ...data,
            timetableTo: data.timetableFrom,
            timetableSelectedItem: item,
            buildings: data.allBuildings,
            instructorTimetables,
            emptySlots,
        });

        const rightIndex = rightSideTabTimetables.findIndex((t) => t.objectID === data.timetableFrom?.objectID);
        setRightSideTabTimetables(rightSideTabTimetables);
        setRightIndex(rightIndex !== -1 ? rightIndex : 0);
    };

    const renderLabel = (fromItem, weekday, timeIn, instructor, room) => {
        const { objectTypeID, course } = fromItem;

        let name = '';

        if (objectTypeID === 1) {
            name = `${course.subject} ${course.courseNumber}`;
        } else if (objectTypeID === 2) {
            name = `${course.subject} ${course.courseNumber}`;
        } else if (objectTypeID === 3) {
            name = `${course.subject} ${course.courseNumber}`;
        }

        //instructor swap / assignment
        if (type === 1 || type === 3) return `${name} to ${instructor.firstName + ' ' + instructor.lastName}`;
        //room swap / assignment
        if (type === 2 || type === 4) return `${name} to room ${room.roomNumber}`;

        return `${name} to ${weekday.substring(0, 2)} ${timeIn.substring(0, timeIn.length - 3)}`;
    };

    //selecting a timeslot on the right side
    const handleSelectTimeslot = async ({ weekDayIndex, timeIn }) => {
        const { id, objectTypeID, objectID, room, instructors, timetableFunctions } = data.timetableSelectedItem;

        // Get instance of previous changes
        const allChanges = [...data.timetableMoves];
        const weekDay = weekdays[weekDayIndex];
        const instructorIds = instructors.map((i) => i.instructorID);

        //each instructor => function id would be a battleship change

        const newChange = new BattleShipMove(
            type,
            data.changeIndex,
            objectID,
            objectTypeID,
            timeIn,
            weekDayIndex + 1,
            renderLabel(data.timetableSelectedItem, weekDay, timeIn, null, null),
            id,
            instructorIds,
            data.timetableTo.objectTypeID === 3 ? data.timetableTo.objectID : room ? room.roomID : null,
            null,
            null,
            null,
            null,
            timetableFunctions,
        );

        allChanges.push(newChange);

        const updatedTimetables = await GetUpdatedTimetables(termSchoolID, objectTypeID, objectID, allChanges, week);
        const timetableTo = updatedTimetables.find((t) => t.objectID === objectID && t.objectTypeID === objectTypeID);

        setData({ ...data, timetablePendingChange: newChange, timetableTo, emptySlots: [] });

        // for SET to SET, update all the timetables (mainly non-primary) in the group with the latest changes
        if (type === 0) {
            const updatedRightSideTabTimetables = [];

            rightSideTabTimetables.forEach((rst) => {
                const index = updatedTimetables.findIndex((t) => t.objectID == rst.objectID && t.objectTypeID == rst.objectTypeID);

                if (index !== -1) {
                    updatedRightSideTabTimetables.push(updatedTimetables[index]);
                }
            });

            setRightSideTabTimetables(updatedRightSideTabTimetables);
        }
    };

    const viewTimetableByChangeIndex = async (changeIndex) => {
        const changesBefore = [...data.timetableMoves].slice(0, changeIndex);

        const change = data.timetableMoves[changeIndex];

        setType(parseInt(change.changeType));

        const objectId = change.objectId;

        const timetableBefore = await GetUpdatedTimetable(termSchoolID, change.objectTypeId, objectId, changesBefore, week);

        const setsSelected = [],
            departmentSelected = [],
            instructorsSelectedFrom = [],
            instructorsSelectedTo = [],
            buildingSelectedFrom = [],
            buildingSelectedTo = [],
            roomsSelectedFrom = [],
            roomsSelectedTo = [];

        //load the selected set for the left timetable
        if (change.changeType === 0 || change.changeType === 3 || change.changeType === 4) {
            //TODO: Get the data from the API
            const set = timetableBefore.timetable.timetables[0].sets.find((s) => s.setID === objectId);

            if (set) {
                setsSelected.push(set);
                const department = data.departments.find((d) => d.departmentCode === set.departmentCode);
                departmentSelected.push(department);
            }
        }

        if (change.changeType === 1) {
            const fromInstructor = data.allInstructors.find((i) => i.instructorID === change.objectId);
            const toInstructor = data.allInstructors.find((i) => i.instructorID === change.toInstructorObjectId);

            instructorsSelectedFrom.push(fromInstructor);
            instructorsSelectedTo.push(toInstructor);
        }

        if (change.changeType === 2) {
            const buildingFromId = change.fromBuildingObjectId;
            const buildingToId = change.toBuildingObjectId;

            const buildingFrom = data.allBuildings.find((b) => b.buildingID === buildingFromId);
            const buildingTo = data.allBuildings.find((b) => b.buildingID === buildingToId);

            const fromBuildingRooms = await _api.GetBuildingRooms(buildingFrom.buildingID);
            const toBuildingRooms = await _api.GetBuildingRooms(buildingTo.buildingID);

            const roomsFrom = fromBuildingRooms.find((r) => r.roomID === change.objectId);
            const roomsTo = toBuildingRooms.find((r) => r.roomID === change.roomId);

            buildingSelectedFrom.push(buildingFrom);
            buildingSelectedTo.push(buildingTo);
            roomsSelectedFrom.push(roomsFrom);
            roomsSelectedTo.push(roomsTo);
        }

        if (change.changeType === 3) {
            const toInstructor = data.allInstructors.find((i) => i.instructorID === change.toInstructorObjectId);
            instructorsSelectedTo.push(toInstructor);
        }

        if (change.changeType === 4) {
            const buildingToId = change.toBuildingObjectId;
            const buildingTo = data.allBuildings.find((b) => b.buildingID === buildingToId);
            const toBuildingRooms = await _api.GetBuildingRooms(buildingTo.buildingID);
            const roomsTo = toBuildingRooms.find((r) => r.roomID === change.roomId);

            buildingSelectedTo.push(buildingTo);
            roomsSelectedTo.push(roomsTo);
        }

        const changesAfter = [...data.timetableMoves].slice(0, changeIndex + 1);

        let timetableAfter = null;

        //For SET -> INSTRUCTOR, load the Instructor timetable on the right
        //For SET -> ROOM, load the Room timetable on the right

        if (change.changeType === 3) {
            timetableAfter = await GetUpdatedTimetable(termSchoolID, 2, change.toInstructorObjectId, changesAfter, week);
        } else if (change.changeType === 4) {
            timetableAfter = await GetUpdatedTimetable(termSchoolID, 3, change.roomId, changesAfter, week);
        } else if (change.changeType === 2) {
            timetableAfter = await GetUpdatedTimetable(termSchoolID, 3, change.roomId, changesAfter, week);
        } else if (change.changeType === 1) {
            timetableAfter = await GetUpdatedTimetable(termSchoolID, 2, change.toInstructorObjectId, changesAfter, week);
        } else {
            timetableAfter = await GetUpdatedTimetable(termSchoolID, change.objectTypeId, change.objectId, changesAfter, week);
        }

        // The pending change is stored on the changes array
        const timetablePendingChange = data.timetableMoves[changeIndex];

        // To get the actual selected timetable item, we need to search for it in the before timetable by id
        const timetableSelectedItem = timetableBefore.timetable.timetables.find((o) => o.id === timetablePendingChange.functionId);

        setData({
            ...data,
            departmentSelected: departmentSelected,
            setsSelected: setsSelected,

            instructorsSelectedFrom: instructorsSelectedFrom,
            instructorsSelectedTo: instructorsSelectedTo,

            changeIndex,
            timetableFrom: timetableBefore,
            timetableTo: timetableAfter,

            timetableSelectedItem,
            timetablePendingChange,
            buildingSelectedFrom,
            buildingSelectedTo,
            roomsSelectedFrom,
            roomsSelectedTo,

            setTimetables: [],
            instructorTimetables: [],
            emptySlots: [],
        });
    };

    const handleExport = () => {
        _exporter.save();
    };

    const handleDeleteChanges = () => {
        const nextChanges = produce(data.timetableMoves, (draft) => {
            return draft.slice(0, deleteConfirm.index);
        });

        const nextChangesFrom = produce(data.changesFrom, (draft) => {
            return draft.slice(0, deleteConfirm.index);
        });

        const nextExcelExportData = produce(excelExportData, (draft) => {
            return draft.slice(0, deleteConfirm.index);
        });

        setExcelExportData(nextExcelExportData);

        setData({
            ...data,
            // Increase change index value
            changeIndex: deleteConfirm.index,
            // We'll set the base timetable but only if it doesn't exist yet
            baseObjectID: data.baseObjectID || data.timetableFrom.objectID,
            baseObjectTypeID: data.baseObjectTypeID || data.timetableFrom.objectTypeID,
            // Merge all changes with latest change
            timetableMoves: nextChanges,
            // Reset default parameter
            timetableFrom: null,
            timetableTo: null,

            timetableSelectedItem: null,
            timetablePendingChange: null,

            departmentSelected: [],
            setsSelected: [],
            setTimetables: [],

            instructorsSelectedFrom: [],
            instructorsSelectedTo: [],
            instructorTimetables: [],

            buildingSelectedFrom: [],
            buildingSelectedTo: [],

            roomsSelectedFrom: [],
            roomsSelectedTo: [],

            changesFrom: nextChangesFrom,
        });

        updateLocalStorage(nextChanges, nextExcelExportData, nextChangesFrom);

        handleCloseDeleteConfirm();
    };

    const handleCloseDeleteConfirm = () => setDeleteConfirm({ active: false, index: null });

    /*
     *  SET TO SET
     *      - The set selection and timetable selected are kept
     *      - Update setTimetables list
     *  INSTRUCTOR TO INSTRUCTOR
     *      - The instructorSelectedTo and instructorSelectedFrom are kept
     *      - timetableFrom needs to be updated to reflect the moved course
     */
    const handleApproveChange = async () => {
        const timetableChanges = [...data.timetableMoves, data.timetablePendingChange];

        let timetableFrom = null;
        let timetableTo = null;
        let setTimetables = [];

        // SET to SET or INSTRUCTOR to SET
        /*
          - Update all the SET timetables on the right side with the latest moves
          - Keep the right side timetable to the already selected instructor
         */
        if (type === 0 || type === 3) {
            const updatedTimetables = await GetUpdatedTimetables(termSchoolID, 1, data.timetableSelectedItem.objectID, timetableChanges, week);

            setTimetables = data.setTimetables.map((st) => {
                if (st.objectID === data.timetableTo.objectID) {
                    return { ...data.timetableTo };
                }

                const updatedTimetable = updatedTimetables.find((ut) => ut.objectID === st.objectID && ut.objectTypeID === st.objectTypeID);

                if (updatedTimetable) {
                    return updatedTimetable;
                }

                return st;
            });

            const newTimetableFrom = updatedTimetables.find((t) => t.objectID === data.timetableSelectedItem.objectID && t.objectTypeID === data.timetableSelectedItem.objectTypeID);

            timetableFrom = { ...newTimetableFrom };
            data.changesFrom.push({ ...data.timetableFrom });
            timetableTo = data.timetableTo;
        }

        //INSTRUCTOR TO INSTRUCTOR timetable
        /*
         - Keep the left side instructor timetable open and update it with latest moves
         */
        if (type === 1) {
            const updatedTimetables = await GetUpdatedTimetables(termSchoolID, 2, data.timetableSelectedItem.objectID, timetableChanges, week);
            timetableFrom = updatedTimetables.find((ut) => ut.objectID === data.timetableSelectedItem.objectID && ut.objectTypeID === data.timetableSelectedItem.objectTypeID);
            timetableTo = updatedTimetables.find((ut) => ut.objectID === data.timetableTo.objectID && ut.objectTypeID === data.timetableTo.objectTypeID);
        }

        setData({
            ...data,
            // Increase change index value
            changeIndex: data.changeIndex + 1,
            // We'll set the base timetable but only if it doesn't exist yet
            baseObjectID: data.baseObjectID || data.timetableFrom.objectID,
            baseObjectTypeID: data.baseObjectTypeID || data.timetableFrom.objectTypeID,
            // Merge all changes with latest change
            timetableMoves: timetableChanges,
            // Reset default parameter
            timetableSelectedItem: null,
            timetableFrom,
            timetableTo,

            departmentSelected: type === 0 || type === 3 ? data.departmentSelected : [],
            setsSelected: type === 0 || type === 3 ? data.setsSelected : [],

            setTimetables,

            timetablePendingChange: null,

            instructorsSelectedFrom: type === 1 ? data.instructorsSelectedFrom : [],
            instructorsSelectedTo: type === 3 || type === 1 ? data.instructorsSelectedTo : [],

            buildingSelectedFrom: [],
            buildingSelectedTo: [],

            roomsSelectedFrom: [],
            roomsSelectedTo: [],

            instructorTimetables: [],

            emptySlots: [],
        });

        handleAddExcelExportData(data.changesFrom);
    };

    const handleAddChange = () => {
        setData({
            ...data,
            // Jump back to the end of queue
            changeIndex: data.timetableMoves.length,
            // Reset default parameter
            timetableFrom: null,
            timetableTo: null,

            timetableSelectedItem: null,
            timetablePendingChange: null,

            departmentSelected: [],
            setsSelected: [],
            setTimetables: [],

            instructorsSelectedFrom: [],
            instructorsSelectedTo: [],

            buildingSelectedFrom: [],
            buildingSelectedTo: [],

            roomsSelectedFrom: [],
            roomsSelectedTo: [],

            instructorTimetables: [],
        });
    };

    const handleAddExcelExportData = (timetableFrom) => {
        const { functionId, timetableFunctions } = data.timetablePendingChange;

        const timetableChanges = [...data.timetableMoves, data.timetablePendingChange];

        let initialClassData = null;
        let changedClassData = null;

        if (data.timetableFrom.timetable.timetables.some((t) => t.id === functionId)) initialClassData = data.timetableFrom.timetable.timetables.find((t) => t.id === functionId);

        if (data.timetableFrom.timetable.conflicts.some((t) => t.id === functionId)) initialClassData = data.timetableFrom.timetable.conflicts.find((t) => t.id === functionId);

        if (data.timetableTo.timetable.timetables.some((t) => t.id === functionId)) changedClassData = data.timetableTo.timetable.timetables.find((t) => t.id === functionId);

        if (data.timetableTo.timetable.conflicts.some((t) => t.id === functionId)) changedClassData = data.timetableTo.timetable.conflicts.find((t) => t.id === functionId);

        if (!initialClassData || !changedClassData) {
            toast.error('Error creating excel data');
            Sentry.captureException(new Error('Battleship excel creation failed'), (scope) => {
                scope.setContext('data', {
                    termSchoolID,
                    week,
                    functionId,
                    timetableFunctions,
                    timetableFrom: timetableContainerIds(data.timetableFrom),
                    timetableTo: timetableContainerIds(data.timetableTo),
                    timetableChanges: battleshipMoveIds(timetableChanges),
                });

                return scope;
            });
            return;
        }

        const index = excelExportData.length + 1;
        const change = firstName.substr(0, 1) + lastName.substr(0, 1) + ' ' + index;
        const action = type === 1 || type === 3 ? 'assign' : 'move';
        let crn = '';
        let changeType = '';
        const course = initialClassData.course.subject + ' ' + initialClassData.course.courseNumber;
        let block = '';
        let fromDayOfWeek = '';
        let fromTimeIn = '';
        let fromTimeOut = '';

        let toDayOfWeek = '';
        let toTimeIn = '';
        let toTimeOut = '';

        let fromInstructor = '';
        let toInstructor = '';
        let bcitid = '';
        let fromRoom = '';
        let toRoom = '';

        //assign datetime data
        if (type === 0 || type === 2 || type === 4) {
            fromDayOfWeek = initialClassData.dayOfWeek;
            fromTimeIn = initialClassData.timeIn;
            fromTimeOut = initialClassData.timeOut;

            toDayOfWeek = changedClassData.dayOfWeek;
            toTimeIn = changedClassData.timeIn;
            toTimeOut = changedClassData.timeOut;
        }

        if (type === 1) {
            if (initialClassData.instructors && changedClassData.instructors) {
                const fromInstructorObject = initialClassData.instructors.find((i) => i.instructorID === initialClassData.objectID);
                const toInstructorObject = changedClassData.instructors.find((i) => i.instructorID === changedClassData.objectID);

                fromInstructor = fromInstructorObject.firstName + ' ' + fromInstructorObject.lastName;
                toInstructor = toInstructorObject.firstName + ' ' + toInstructorObject.lastName;
                bcitid = toInstructorObject.bcitid;
            }
        }

        if (type === 2) {
            if (initialClassData.buildingCode && initialClassData.room) {
                fromRoom = initialClassData.buildingCode + ' ' + initialClassData.room.roomNumber;
            }

            if (changedClassData.buildingCode && changedClassData.room) {
                toRoom = changedClassData.buildingCode + ' ' + changedClassData.room.roomNumber;
            }
        }

        if (type === 3) {
            if (changedClassData.instructors) {
                toInstructor = changedClassData.instructors[0].firstName + ' ' + changedClassData.instructors[0].lastName;
                bcitid = changedClassData.instructors[0].bcitid;
            }

            if (initialClassData.instructors && initialClassData.instructors.length > 0) {
                fromInstructor = initialClassData.instructors[0].firstName + ' ' + initialClassData.instructors[0].lastName;
            }
        }

        //set -> room
        if (type === 4) {
            if (changedClassData.buildingCode && changedClassData.room.roomNumber) {
                toRoom = changedClassData.buildingCode + ' ' + changedClassData.room.roomNumber;
            }

            if (initialClassData.buildingCode && initialClassData.room) {
                fromRoom = initialClassData.buildingCode + ' ' + initialClassData.room.roomNumber;
            }
        }

        const excelData = [];

        changedClassData.sets.forEach((s) => {
            block = s.setCode;
            const sectionId = timetableFunctions.find((f) => f.setId === s.setID).sectionId;

            //set the crn
            if (changedClassData.sections && changedClassData.sections.length > 0) {
                const section = changedClassData.sections.find((s) => s.sectionID === sectionId);
                crn = section.crn;
                changeType = section.classType;
            }

            const excelExport = new ExcelExport(
                termCode,
                change,
                action,
                crn,
                course,
                block,
                changeType,
                fromDayOfWeek,
                fromTimeIn,
                fromTimeOut,
                fromRoom,
                toDayOfWeek,
                toTimeIn,
                toTimeOut,
                toRoom,
                fromInstructor,
                toInstructor,
                bcitid,
                '',
            );

            excelData.push(excelExport);
        });

        setExcelExportData([...excelExportData, ...excelData]);

        updateLocalStorage(timetableChanges, [...excelExportData, ...excelData], timetableFrom);
    };

    const handleTimetableTypeChange = (v) => {
        setType(parseInt(v));

        setData({
            ...data,
            departments: data.allDepartments,
            sets: data.allSets,

            departmentSelected: [],
            setsSelected: [],
            setTimetables: [],

            instructors: data.allInstructors,
            instructorsTo: data.allInstructors,

            rooms: data.allRooms,
            roomsTo: data.allRooms,

            instructorsSelectedFrom: [],
            instructorsSelectedTo: [],
            instructorTimetables: [],

            roomsSelectedFrom: [],
            roomsSelectedTo: [],

            timetableFrom: null,
            timetableTo: null,

            buildingSelectedTo: [],
            buildingSelectedFrom: [],
            emptySlots: [],
        });

        setShowAllInstructors(false);
    };

    //Removed at DC's request. She wants to see all instructors
    const handleShowAllInstructors = () => {
        setShowAllInstructors((state) => !state);
        if (showAllInstructors) {
            setData({
                ...data,
                instructorsTo: data.setInstructors,
            });
        } else {
            setData({
                ...data,
                instructorsTo: data.allInstructors,
            });
        }
    };

    const handleDeleteLocalChanges = () => {
        deleteLocalStorage(LOCAL_STORAGE_KEY);
        setConfirmDeleteLocal(false);
    };

    const handleLoadLocalChanges = () => {
        const localStorageData = getLocalStorage(LOCAL_STORAGE_KEY);

        setData({
            ...data,
            timetableMoves: localStorageData.timetableMoves,
            changeIndex: localStorageData.timetableMoves.length,
            changesFrom: localStorageData.changesFrom,
        });

        setExcelExportData(localStorageData.excelExportData);

        setConfirmDeleteLocal(false);
    };

    const updateLocalStorage = (timetableMoves, excelData, changesFrom) => {
        setLocalStorage(LOCAL_STORAGE_KEY, {
            timetableMoves: timetableMoves,
            excelExportData: excelData,
            lastUpdated: Date.now(),
            changesFrom: changesFrom,
        });
    };

    /*
        - Render the left side timetable for SET to SET and SET to INSTRUCTOR
        - only one timetable is rendered
        - when the leftIndex is updated, handleLeftSideSetTabChange will select the timetableTo
     */
    const renderLeftSideSetTimetable = (props: any, setTimetables: Array<any>, leftTabIndex: number) => {
        // const setTimetable = setTimetables[leftTabIndex];
        const setTimetable = data.timetableFrom;

        // if (!setTimetable) {
        //     toast.info('No data exists for selected timetable');
        //     setTimetable = data.setTimetables;
        // }

        return (
            <>
                <Grid key={setTimetable.objectID}>
                    <TimetableContainer>
                        <Timetable
                            {...props}
                            type={type}
                            index="from"
                            schoolID={schoolID}
                            termSchoolID={termSchoolID}
                            onSelectItem={handleSelectTimetableItem}
                            selectedItem={data.timetableSelectedItem}
                            timetable={setTimetable}
                            isTimeTravel={data.changeIndex !== data.timetableMoves.length}
                        />
                    </TimetableContainer>
                </Grid>
            </>
        );
    };

    /*
      - Render the left side timetable for SET to SET
      - only one timetable is rendered
      - when the leftIndex is updated, handleLeftSideSetTabChange will select the timetableTo
      - tabs display multiple timetable but only the timetable being mirrored is editable
   */
    const renderRightSideSetTimetable = (props: any, rightSideTabTimetables: Array<any>, rightTabIndex: number, data: any) => {
        if (!rightSideTabTimetables.length) return null;
        if (!data.timetableFrom) return null;

        let timetable = rightSideTabTimetables[rightTabIndex];

        //the rightTabIndex is not yet set to the correct value
        if (!timetable) return null;

        const timetableProps = {
            emptySlots: [],
            onSelectTimeslot: undefined,
            selectedItem: undefined,
            changedTimeslot: undefined,
            label: undefined,
        };

        if (timetable?.objectID == data.timetableFrom.objectID) {
            timetableProps.onSelectTimeslot = handleSelectTimeslot;
            timetableProps.selectedItem = data.timetableSelectedItem;
            timetableProps.changedTimeslot = data.timetablePendingChange;
            timetableProps.emptySlots = data.emptySlots;
            timetable = data.timetableTo;
        }

        //when viewing a past change, tabs will not be displayed so add a label to the top of the timetable
        if (data.changeIndex !== data.timetableMoves.length) {
            timetableProps.label = data?.timetableFrom?.name;
        }

        return (
            <>
                <TimetableContainer>
                    <Timetable
                        {...props}
                        {...timetableProps}
                        type={type}
                        index="to"
                        schoolID={schoolID}
                        termSchoolID={termSchoolID}
                        sets={data.sets}
                        timetable={timetable}
                        isTimeTravel={data.changeIndex !== data.timetableMoves.length}
                    />
                </TimetableContainer>
            </>
        );
    };

    // SET to SET or SET to INSTRUCTOR set tab change on the right side
    const handleLeftSideSetTabChange = (leftIndex: number) => {
        if (type !== 0 && type !== 3) return;

        if (!data.setsSelected.length && !data.setTimetables.length) return;

        const timetable = data.setTimetables[leftIndex];

        const timetableFrom = _.cloneDeep(timetable);
        const timetableTo = _.cloneDeep(timetable);

        if (type === 0) {
            setData({
                ...data,
                timetableFrom: timetableFrom,
                timetableTo: timetableTo,
                timetablePendingChange: null,
                timetableSelectedItem: null,
                emptySlots: [],
            });

            setRightSideTabTimetables([]);
        }

        if (type === 3) {
            setData({
                ...data,
                timetableFrom: timetable,
                timetableTo: null,
                timetableSelectedItem: null,
                instructorsSelectedTo: [],
                emptySlots: [],
            });
        }

        setLeftIndex(leftIndex);
    };

    const handleRemoveLeftSideTab = (leftTabIndex: number, objectID: number) => {
        const setTimetables = data.setTimetables;
        const setTimetablesFiltered = setTimetables.filter((t) => t.objectID !== objectID);
        let timetableFrom = data.timetableFrom;

        let indexToSet = leftTabIndex;

        //if the first tab is being removed, keep the first tab selected else select the tab to the left
        if (leftTabIndex === 0) {
            indexToSet = 0;
        }

        if (leftTabIndex === leftIndex && leftTabIndex > 0) {
            indexToSet = leftIndex - 1;
            setRightSideTabTimetables([]);
            timetableFrom = setTimetablesFiltered[indexToSet];
        }

        if (leftTabIndex === leftIndex && leftTabIndex === 0) {
            indexToSet = 0;
            setRightSideTabTimetables([]);
            timetableFrom = setTimetablesFiltered[indexToSet];
        }

        if (type === 0) {
            setData({
                ...data,
                timetableFrom: timetableFrom,
                timetableTo: null,
                timetablePendingChange: null,
                timetableSelectedItem: null,
                emptySlots: [],
                setTimetables: setTimetablesFiltered,
            });
        }

        if (type === 3) {
            setData({
                ...data,
                timetableFrom: timetableFrom,
                timetableTo: null,
                timetableSelectedItem: null,
                instructorsSelectedTo: [],
                emptySlots: [],
                setTimetables: setTimetablesFiltered,
            });
        }

        setLeftIndex(indexToSet);
    };

    return (
        <>
            <Container>
                <Spacer mb={10}>
                    <Grid container alignItems={'center'} justifyContent={'space-between'}>
                        <StyledTabsContainer
                            value={type}
                            onChange={(e, v) => handleTimetableTypeChange(v)}
                            TabIndicatorProps={{
                                style: {
                                    display: 'none',
                                },
                            }}
                        >
                            <StyledTab
                                label={
                                    <div>
                                        <DateIcon style={{ verticalAlign: 'middle', marginRight: '5px' }} /> SET TO SET
                                    </div>
                                }
                            />
                            <StyledTab
                                label={
                                    <div>
                                        <InstructorIcon style={{ verticalAlign: 'middle', marginRight: '5px' }} /> INSTRUCTOR TO INSTRUCTOR
                                    </div>
                                }
                            />
                            <StyledTab
                                label={
                                    <div>
                                        <RoomIcon style={{ verticalAlign: 'middle', marginRight: '5px' }} /> ROOM TO ROOM
                                    </div>
                                }
                            />
                            <StyledTab
                                label={
                                    <div>
                                        <AddInstructorIcon style={{ verticalAlign: 'middle', marginRight: '5px' }} /> SET TO INSTRUCTOR
                                    </div>
                                }
                            />
                            <StyledTab
                                label={
                                    <div>
                                        <AddRoomIcon style={{ verticalAlign: 'middle', marginRight: '5px' }} /> SET TO ROOM
                                    </div>
                                }
                            />
                        </StyledTabsContainer>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flex: 1 }}>
                            <Box sx={{ marginRight: '10px' }}></Box>
                            <TextField
                                label={'Week'}
                                size={'small'}
                                type="number"
                                onChange={(e) => setWeek(parseInt(e.target.value))}
                                value={week}
                                variant="outlined"
                                // Disabled week selector when time traveling
                                disabled={data.changeIndex !== data.timetableMoves.length}
                            />
                        </Box>
                    </Grid>
                </Spacer>
                <Grid container>
                    <LeftTimetableContainer>
                        {(type === 0 || type === 3) && (
                            <>
                                <TimetableHeader>
                                    {isCentralTimetablerView && (
                                        <DropdownContainer>
                                            <CustomDropdown placeholder="Term" data={filteredTermSchools} name="code" field="termSchoolId" size={'sm'} onChange={handleTermChange} value={term} />
                                        </DropdownContainer>
                                    )}
                                    <DropdownContainer>
                                        <CustomDropdown
                                            isSearch
                                            placeholder="Program"
                                            data={data.departments}
                                            name="departmentCode"
                                            field="departmentID"
                                            onRemove={onRemoveDepartment}
                                            disabled={(data.changeIndex !== data.timetableMoves.length || (isCentralTimetablerView && !term))}
                                            onChange={(item) => onDepartmentChange(item, true)}
                                            value={data.departmentSelected[0]}
                                            // TODO: uncomment for multiSelect
                                            // multiSelect
                                            // value={data.departmentSelected}
                                        />
                                    </DropdownContainer>
                                    <DropdownContainer>
                                        {Array.isArray(data.departmentSelected) && data.departmentSelected.length > 0 && (
                                            <CustomDropdown
                                                isSearch
                                                // multiSelect
                                                placeholder="Set(s)"
                                                size="sm"
                                                data={data.sets}
                                                name="setCode"
                                                field="setID"
                                                onChange={onSetsChange}
                                                // value={data.setsSelected}
                                                disabled={data.changeIndex !== data.timetableMoves.length}
                                            />
                                        )}
                                    </DropdownContainer>
                                </TimetableHeader>
                            </>
                        )}

                        {type === 1 && (
                            <TimetableHeader>
                                {isCentralTimetablerView && (
                                    <DropdownContainer>
                                        <CustomDropdown placeholder="Term" data={filteredTermSchools} name="code" field="termSchoolId" size={'sm'} onChange={handleTermChange} value={term} />
                                    </DropdownContainer>
                                )}
                                <DropdownContainer>
                                    <CustomDropdown
                                        placeholder="Instructor"
                                        data={data.instructors}
                                        value={data.instructorsSelectedFrom[0] || null}
                                        onChange={(item) => handleInstructorChange(item, 'from')}
                                        name="fullName"
                                        field="instructorID"
                                        isSearch
                                        disabled={(data.changeIndex !== data.timetableMoves.length || (isCentralTimetablerView && !term))}
                                        // multiSelect
                                    />
                                </DropdownContainer>
                            </TimetableHeader>
                        )}

                        {type === 2 && (
                            <>
                                <TimetableHeader>
                                    {isCentralTimetablerView && (
                                        <DropdownContainer>
                                            <CustomDropdown placeholder="Term" data={filteredTermSchools} name="code" field="termSchoolId" size={'sm'} onChange={handleTermChange} value={term} />
                                        </DropdownContainer>
                                    )}
                                    <DropdownContainer>
                                        <CustomDropdown
                                            data={data.buildings}
                                            placeholder="Building"
                                            name="buildingCode"
                                            field="buildingID"
                                            value={data.buildingSelectedFrom[0] || null}
                                            onChange={(item) => handleBuildingChange(item, 'from')}
                                            isSearch
                                            disabled={(data.changeIndex !== data.timetableMoves.length || (isCentralTimetablerView && !term))}
                                        />
                                    </DropdownContainer>
                                    <DropdownContainer>
                                        {Array.isArray(data.buildingSelectedFrom) && data.buildingSelectedFrom.length > 0 && (
                                            <CustomDropdown
                                                data={data.rooms}
                                                placeholder="Rooms"
                                                name="roomNumber"
                                                field="roomID"
                                                value={data.roomsSelectedFrom[0] || null}
                                                onChange={(item) => onRoomChange(item, 'from')}
                                                size="sm"
                                                disabled={(data.changeIndex !== data.timetableMoves.length || (isCentralTimetablerView && !term))}
                                            />
                                        )}
                                    </DropdownContainer>
                                </TimetableHeader>
                            </>
                        )}

                        {type === 4 && (
                            <>
                                <TimetableHeader>
                                    {isCentralTimetablerView && (
                                        <DropdownContainer>
                                            <CustomDropdown placeholder="Term" data={filteredTermSchools} name="code" field="termSchoolId" size={'sm'} onChange={handleTermChange} value={term} />
                                        </DropdownContainer>
                                    )}
                                    <DropdownContainer>
                                        <CustomDropdown
                                            data={data.departments}
                                            value={data.departmentSelected[0] || null}
                                            name="departmentCode"
                                            field="departmentID"
                                            onChange={(item) => onDepartmentChange(item, false)}
                                            placeholder={'Program'}
                                            isSearch
                                            disabled={(data.changeIndex !== data.timetableMoves.length || (isCentralTimetablerView && !term))}
                                        />
                                    </DropdownContainer>
                                    <DropdownContainer>
                                        {Array.isArray(data.departmentSelected) && data.departmentSelected.length > 0 && (
                                            <CustomDropdown
                                                data={data.sets}
                                                size="sm"
                                                value={data.setsSelected[0] || null}
                                                name="setCode"
                                                field="setID"
                                                onChange={onSetChange}
                                                placeholder={'Sets'}
                                                disabled={(data.changeIndex !== data.timetableMoves.length || (isCentralTimetablerView && !term))}
                                            />
                                        )}
                                    </DropdownContainer>
                                </TimetableHeader>
                            </>
                        )}

                        {/*
                          - SET to SET OR SET to INSTRUCTOR
                          - Displays multiple SET timetables in tabs
                        */}
                        {(type === 0 || type === 3) && data.setTimetables.length > 0 && data.changeIndex === data.timetableMoves.length && (
                            <div style={{ marginLeft: '15px' }}>
                                <TimetableTabsContainer>
                                    {data.setTimetables.map((t, i) => {
                                        let label = `Tab ${i}`;

                                        const set = data.allSets.find((s) => s.setID === t.objectID);
                                        if (set) label = set.setCode;

                                        return (
                                            <TimetableTabs
                                                key={t.objectID}
                                                value={leftIndex}
                                                onChange={() => handleLeftSideSetTabChange(i)}
                                                label={label}
                                                tabvalue={i}
                                                onRemove={() => handleRemoveLeftSideTab(i, t.objectID)}
                                            />
                                        );
                                    })}
                                </TimetableTabsContainer>

                                {/* Only render the timetable selected in the tab */}
                                {renderLeftSideSetTimetable(props, data.setTimetables, leftIndex)}
                            </div>
                        )}

                        {/*
                          - SET to SET OR SET to INSTRUCTOR
                          - Use the normal grid when viewing a previously made change
                        */}
                        {(type === 0 || type === 3) && data.timetableFrom && data.changeIndex !== data.timetableMoves.length && (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Timetable
                                        {...props}
                                        label={data.timetableFrom.name}
                                        isSelected={true}
                                        type={type}
                                        index="from"
                                        schoolID={schoolID}
                                        termSchoolID={termSchoolID}
                                        sets={data.sets}
                                        onSelectItem={handleSelectTimetableItem}
                                        selectedItem={data.timetableSelectedItem}
                                        timetable={data.timetableFrom}
                                        isTimeTravel={data.changeIndex !== data.timetableMoves.length}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {type !== 0 && type !== 3 && data.timetableFrom && (
                            <div style={{ marginLeft: '15px' }}>
                                <TimetableContainer>
                                    <Timetable
                                        {...props}
                                        isSelected={true}
                                        type={type}
                                        index="from"
                                        schoolID={schoolID}
                                        termSchoolID={termSchoolID}
                                        sets={data.sets}
                                        onSelectItem={handleSelectTimetableItem}
                                        selectedItem={data.timetableSelectedItem}
                                        timetable={data.timetableFrom}
                                        isTimeTravel={data.changeIndex !== data.timetableMoves.length}
                                    />
                                </TimetableContainer>
                            </div>
                        )}
                    </LeftTimetableContainer>

                    {/* ---- RIGHT SIDE ---- */}
                    <RightTimetableContainer type={type} isView={data.changeIndex !== data.timetableMoves.length}>
                        {(type === 1 || type === 3) && (
                            <TimetableHeader>
                                <>
                                    <DropdownContainer>
                                        <CustomDropdown
                                            placeholder="Instructor"
                                            data={data.instructorsTo.filter((o) => o.instructorID !== data.instructorsSelectedFrom?.[0]?.instructorID)}
                                            value={data.instructorsSelectedTo[0] || null}
                                            name="fullName"
                                            field="instructorID"
                                            onChange={(item) => handleInstructorChange(item, 'to')}
                                            disabled={
                                                data.changeIndex !== data.timetableMoves.length ||
                                                (type === 1 && !data.instructorsSelectedFrom[0]) ||
                                                (type === 3 && !data.setsSelected[0]) ||
                                                !data.timetableSelectedItem
                                            }
                                            isSearch
                                            showAll={showAllInstructors}
                                        />
                                    </DropdownContainer>

                                    {!showAllInstructors && data.instructorsSelectedTo.length > 0 && (
                                        <div>
                                            <Tooltip title="Prev Instructor">
                                                <StyledFab
                                                    size="small"
                                                    disabled={
                                                        data.changeIndex !== data.timetableMoves.length ||
                                                        // !data.instructorsSelectedFrom[0] ||
                                                        !data.timetableSelectedItem
                                                    }
                                                    onClick={handlePreviousInstructorClick}
                                                >
                                                    <ArrowBack />
                                                </StyledFab>
                                            </Tooltip>

                                            <Tooltip title="Next Instructor">
                                                <StyledFab
                                                    size="small"
                                                    disabled={
                                                        data.changeIndex !== data.timetableMoves.length ||
                                                        // !data.instructorsSelectedFrom[0] ||
                                                        !data.timetableSelectedItem
                                                    }
                                                    onClick={handleNextInstructorClick}
                                                >
                                                    <ArrowForward />
                                                </StyledFab>
                                            </Tooltip>
                                        </div>
                                    )}
                                </>
                            </TimetableHeader>
                        )}

                        {(type === 2 || type === 4) && (
                            <>
                                <TimetableHeader>
                                    <DropdownContainer>
                                        <CustomDropdown
                                            data={data.buildingsTo}
                                            placeholder="Building"
                                            name="buildingCode"
                                            field="buildingID"
                                            value={data.buildingSelectedTo[0] || null}
                                            onChange={(item) => handleBuildingChange(item, 'to')}
                                            isSearch
                                            disabled={
                                                data.changeIndex !== data.timetableMoves.length ||
                                                (type === 2 && !data.roomsSelectedFrom[0]) ||
                                                (type === 4 && !data.setsSelected[0]) ||
                                                !data.timetableSelectedItem
                                            }
                                        />
                                    </DropdownContainer>
                                    <DropdownContainer>
                                        {Array.isArray(data.buildingSelectedTo) && data.buildingSelectedTo.length > 0 && (
                                            <CustomDropdown
                                                data={data.roomsTo}
                                                size="sm"
                                                value={data.roomsSelectedTo[0] || null}
                                                name="roomNumber"
                                                field="roomID"
                                                onChange={(item) => onRoomChange(item, 'to')}
                                                placeholder={'Rooms'}
                                                disabled={data.changeIndex !== data.timetableMoves.length}
                                            />
                                        )}
                                    </DropdownContainer>
                                </TimetableHeader>
                            </>
                        )}

                        {type !== 0 && data.timetableTo && (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Timetable
                                        {...props}
                                        type={type}
                                        index="to"
                                        schoolID={schoolID}
                                        termSchoolID={termSchoolID}
                                        sets={data.sets}
                                        onSelectTimeslot={handleSelectTimeslot}
                                        selectedItem={data.timetableSelectedItem}
                                        timetable={data.timetableTo}
                                        changedTimeslot={data.timetablePendingChange}
                                        isTimeTravel={data.changeIndex !== data.timetableMoves.length}
                                        emptySlots={data.emptySlots}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {/*
                           - Right side for SET to SET
                           - Displays multiple timetables
                         */}
                        {type === 0 && data.timetableTo && (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    {data.changeIndex === data.timetableMoves.length && (
                                        <TimetableTabsContainer>
                                            {rightSideTabTimetables.map((t, i) => {
                                                let label = `Tab ${i}`;

                                                if (t.name) {
                                                    label = t.name;
                                                }

                                                return <TimetableTabs key={t.objectID} onChange={() => setRightIndex(i)} isReadOnly={true} value={rightIndex} label={label} tabvalue={i} />;
                                            })}
                                        </TimetableTabsContainer>
                                    )}

                                    {renderRightSideSetTimetable(props, rightSideTabTimetables, rightIndex, data)}
                                </Grid>
                            </Grid>
                        )}
                    </RightTimetableContainer>
                </Grid>
            </Container>

            <BottomContainer active={!!data.timetablePendingChange && data.changeIndex === data.timetableMoves.length}>
                <Button children="Save Changes" onClick={handleApproveChange} variant={'contained'} />
            </BottomContainer>

            <Sidebar active={sidebar}>
                <SidebarToggle onClick={() => setSidebar(!sidebar)} active={sidebar}>
                    <ArrowIcon />
                </SidebarToggle>
                {data.timetableMoves.map((o, i) => {
                    const active = data.changeIndex === i;
                    let fromInstructor;
                    let fromTime;

                    if (o.changeType === 0 || o.changeType === 3) {
                        const timetableBefore = data.changesFrom.find((item) => item.objectID === o.objectId);

                        let timetableSelectedItem = timetableBefore.timetable.timetables.find((t) => t.id === o.functionId);

                        if (!timetableSelectedItem) {
                            timetableSelectedItem = timetableBefore.timetable.conflicts.find((t) => t.id === o.functionId);
                        }

                        // 1. Set to Set
                        if (o.changeType === 0) {
                            fromTime = timetableSelectedItem.dayOfWeek.slice(0, 3) + ' ' + timetableSelectedItem.timeIn.slice(0, 5);
                        }

                        // 3. Set to Instructor
                        if (o.changeType === 3) {
                            fromInstructor = timetableSelectedItem.instructors[0];
                        }
                    }

                    // 2. Instructor to Instructor
                    if (o.changeType === 1) {
                        fromInstructor = data.instructors.find((item) => item.instructorID === o.objectId);
                    }

                    return (
                        <SidebarItem key={o.changeIndex} active={active} sidebarActive={sidebar}>
                            {!sidebar && <SidebarItemIndex children={i + 1} />}
                            <SidebarItemContent active={sidebar} type={o.changeType}>
                                <div style={{ marginBottom: '10px' }}>
                                    <div>
                                        <SidebarInfo type={o.changeType}>
                                            {o.changeType === 0 ? <DateIcon /> : o.changeType === 2 || o.changeType === 4 ? <RoomIcon /> : <InstructorIcon />}
                                        </SidebarInfo>
                                        <SidebarInfo active={active}>{o.label.slice(0, 9)}</SidebarInfo>
                                        {/* TODO: find # of errors and display here */}
                                        {/*<SidebarInfo error> <ErrorIcon />1 </SidebarInfo>*/}
                                    </div>
                                    <div>
                                        <SidebarItemButton active={active} onClick={() => viewTimetableByChangeIndex(i)}>
                                            <Tooltip title="View">
                                                <ViewIcon />
                                            </Tooltip>
                                        </SidebarItemButton>
                                        <SidebarItemButton onClick={() => setDeleteConfirm({ active: true, index: i })}>
                                            <Tooltip title="Delete">
                                                <DeleteIcon />
                                            </Tooltip>
                                        </SidebarItemButton>
                                    </div>
                                </div>
                                <div style={{ justifyContent: 'flex-start' }}>
                                    {o.changeType === 0 && fromTime ? (
                                        <TimeInfo capitalize>{fromTime}</TimeInfo>
                                    ) : fromInstructor ? (
                                        <span>{fromInstructor.firstName + ' ' + fromInstructor.lastName}</span>
                                    ) : (
                                        <span></span>
                                    )}
                                    {o.changeType !== 2 && o.changeType !== 4 && <ChangeIcon id="changeIcon" />}
                                    <TimeInfo capitalize={o.changeType === 0}>{o.changeType === 2 || o.changeType === 4 ? o.label.slice(18) : o.label.slice(13)}</TimeInfo>
                                </div>
                            </SidebarItemContent>
                        </SidebarItem>
                    );
                })}

                {data.changeIndex === data.timetableMoves.length ? (
                    <SidebarItem active sidebarActive={sidebar} lastItem>
                        {!sidebar && <SidebarItemIndex children={data.timetableMoves.length + 1} />}
                        <SidebarItemContent active={sidebar} lastItem>
                            <span>Pending change</span>
                        </SidebarItemContent>
                    </SidebarItem>
                ) : (
                    <SidebarItem active={false} onClick={handleAddChange} sidebarActive={sidebar} lastItem>
                        <SidebarItemIndex>
                            <PlusIcon />
                        </SidebarItemIndex>
                        <SidebarItemContent active={sidebar} lastItem>
                            <span style={{ color: '#B6B6B6' }}>Add a new change</span>
                        </SidebarItemContent>
                    </SidebarItem>
                )}

                {data.timetableMoves.length > 0 && (
                    <SidebarFooter>
                        {sidebar ? (
                            <Button children="Export XLS" onClick={handleExport} style={{ width: '100%' }} />
                        ) : (
                            <Button onClick={handleExport} style={{ width: '40px' }} icon color={theme.colors.green.main}>
                                <ExportIcon />
                            </Button>
                        )}
                    </SidebarFooter>
                )}
            </Sidebar>

            {deleteConfirm.active && (
                <Dialog open={deleteConfirm.active} onClose={handleCloseDeleteConfirm} fullWidth maxWidth="xs">
                    <DialogContent>
                        <Spacer mb={20}>
                            <Typography variant="h4" gutterBottom>
                                Confirmation
                            </Typography>
                            <Typography gutterBottom>{deleteConfirm.index === 0 ? `Delete ALL changes?` : `Delete everything after change ${deleteConfirm.index}?`}</Typography>
                        </Spacer>
                        <Spacer mb={20}>
                            <Button onClick={handleDeleteChanges} children="Confirm" />{' '}
                        </Spacer>
                    </DialogContent>
                </Dialog>
            )}

            {confirmDeleteLocal && (
                <Dialog open={confirmDeleteLocal} onClose={handleDeleteLocalChanges} fullWidth maxWidth="xs">
                    <DialogContent>
                        <Spacer mb={20}>
                            <Typography variant="h4" gutterBottom>
                                Load Saved Moves
                            </Typography>
                            <Typography gutterBottom>
                                {`${getLocalStorage(LOCAL_STORAGE_KEY).timetableMoves.length} timetable move(s) are saved from ${moment(getLocalStorage(LOCAL_STORAGE_KEY).lastUpdated).format(
                                    'dddd, MMMM Do, h:mm a',
                                )}. Would you like to load them?`}
                            </Typography>
                        </Spacer>
                        <Spacer mb={20}>
                            <Button onClick={handleDeleteLocalChanges} children="Delete saved moves" />
                            <Button style={{ marginLeft: '10px' }} onClick={handleLoadLocalChanges} children="Yes" />
                        </Spacer>
                    </DialogContent>
                </Dialog>
            )}

            <KendoExcelExport
                data={excelExportData}
                fileName={moment().format(MOMENT_DATE_FORMAT) + '_' + moment().format(MOMENT_TIME_DISPLAY_FORMAT) + '.xlsx'}
                ref={(exporter) => {
                    _exporter = exporter;
                }}
            >
                <ExcelExportColumn field="term" title="Term" width={70} />
                <ExcelExportColumn field="change" title="Change" width={70} />
                <ExcelExportColumn field="action" title="Action" width={70} />
                <ExcelExportColumn field="crn" title="CRN" width={70} />
                <ExcelExportColumn field="course" title="Course" width={70} />
                <ExcelExportColumn field="block" title="Block" width={70} />
                <ExcelExportColumn field="type" title="Type" width={70} />
                <ExcelExportColumn field="fromDay" title="From Day" width={70} />
                <ExcelExportColumn field="fromTime" title="From Time" width={70} />
                <ExcelExportColumn field="fromRoom" title="From Room" width={70} />
                <ExcelExportColumn field="toDay" title="To Day" width={70} />
                <ExcelExportColumn field="toTime" title="To Time" width={70} />
                <ExcelExportColumn field="toRoom" title="To Room" width={70} />
                <ExcelExportColumn field="from" title="From" width={110} />
                <ExcelExportColumn field="to" title="To" width={110} />
                <ExcelExportColumn field="bannerID" title="Banner ID" width={70} />
                <ExcelExportColumn field="comments" title="Comments" width={400} />
            </KendoExcelExport>
        </>
    );
};

const Container = styled.div`
    position: absolute;
    left: 0;
    width: 100%;
    height: calc(100vh - 62px);
    background: #f0f5f9;
    padding: 20px 100px 0 10px;
    overflow: auto;
`;

const LeftTimetableContainer = styled.div`
    width: 50%;
`;

const RightTimetableContainer = styled.div<RightTimetableContainerProps>`
    width: 50%;
    margin-top: ${(props) => (props.type === 0 ? '50px' : '0px')};
    padding-left: ${(props) => (props.isView ? '10px' : '0px')};
`;

const TimetableContainer = styled.div`
    background-color: #fff;
    padding: 10px 10px 10px 0px;
    margin-right: 15px;
`;

const MultiSelectContainer = styled.div`
    .k-multiselect .k-multiselect-wrap .k-button,
    .k-dropdowntree .k-multiselect-wrap .k-button {
        background: white;
    }
`;

const TimetableHeader = styled.div`
    display: flex;
    align-items: center;
    /* padding: 0 10px; */
`;

const DropdownContainer = styled.div`
    padding: 10px;
    z-index: 10;
`;

const Sidebar = styled(({ active, ...rest }) => <div {...rest} />)`
    position: fixed;
    z-index: 20;
    right: 20px;
    width: ${({ active }) => (active ? '300px' : '60px')};
    height: calc(100vh - 90px);
    background: #fbfbfb;
    box-shadow: -4px 0px 12px rgba(0, 0, 0, 0.06);
    padding-bottom: 80px;
`;

const SidebarFooter = styled.div`
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    padding: 10px;
    border-top: 1px solid #e2e2e2;
`;

const SidebarToggle = styled(({ active, ...rest }) => <div {...rest} />)`
    display: flex;
    justify-content: ${({ active }) => (active ? `flex-end` : `center`)};
    align-items: center;
    padding: 15px 20px;
    cursor: pointer;
    background: ${theme.colors.green.main};
    transform: ${({ active }) => (active ? 'rotate(180deg)' : 'rotate(0deg)')};

    svg {
        width: 8px;
        height: auto;
    }
`;

const SidebarItem = styled(({ active, sidebarActive, ...rest }) => <div {...rest} />)`
    display: flex;
    align-items: center;
    height: ${({ sidebarActive }) => (sidebarActive ? `unset` : '65px')};
    padding: 20px;
    background: ${({ active }) => (active ? '#F0F5F9' : '#fff')};
    margin: 5px;
    filter: drop-shadow(0px 2px 10px rgba(0, 0, 0, 0.05));
    border-radius: 5px;
    font-size: 18px;

    ${({ onClick }) =>
        onClick &&
        css`
            &:hover {
                cursor: pointer;
                background: #e2f2ff;
            }
        `}
    ${({ sidebarActive }) =>
        sidebarActive && {
            padding: `10px`,
            fontSize: `12px`,
        }}
    ${({ lastItem }) =>
        lastItem && {
            height: `65px`,
            justifyContent: 'center',
        }}
`;

const SidebarItemIndex = styled.div`
    display: flex;
    justify-content: center;
    width: 30px;

    svg {
        width: 22px;
        height: auto;
        margin-right: 10px;
    }
`;

const SidebarItemContent = styled(({ active, ...rest }) => <div {...rest} />)`
    display: flex;
    flex-direction: column;
    width: ${({ active }) => (active ? '100%' : 0)};
    overflow: hidden;
    font-size: 14px;
    font-weight: 500;

    div {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    div:last-child {
        // justify-content: flex-start;
    }

    span {
        text-align: center;
    }

    #changeIcon {
        margin: 0px 5px;

        path {
            fill: ${(props) => (props.type === 1 || props.type === 3 ? theme.colors.green.main : theme.colors.blue.main)};
        }
    }

    ${({ lastItem }) =>
        lastItem && {
            span: {
                textAlign: 'left',
            },
        }}
`;

const SidebarItemButton = styled.div<SidebarItemButtonProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 4px;
    cursor: pointer;

    svg {
        &:hover {
            path {
                fill: ${theme.colors.blue.main};
            }
        }

        path {
            fill: ${({ active }) => active && theme.colors.blue.main};
            height: 20px;
            width: auto;
        }
    }
`;

const SidebarInfo = styled.div<SidebarInfoProps>`
    background-color: ${({ active }) => (active ? '#fff' : '#F0F5F9')};
    border-radius: 10px;
    padding: 5px 7px;
    margin-right: 5px;
    font-size: 12px;
    font-weight: 500;

    svg {
        width: 15px;
        height: 15px;

        path {
            fill: #fff;
        }
    }

    ${({ type }) =>
        type === 0 || type === 2 || type === 4
            ? {
                  backgroundColor: theme.colors.blue.main,
              }
            : type === 1 || type === 3
              ? {
                    backgroundColor: theme.colors.green.main,
                }
              : { backgroundColor: '#F0F5F9' }}

    ${({ error }) =>
        error && {
            backgroundColor: `rgba(215, 57, 72, 0.1)`,
            color: theme.colors.red.main,
            svg: {
                path: {
                    fill: theme.colors.red.main,
                },
            },
        }}
`;

const TimeInfo = styled.span<TimeInfoProps>`
    ${({ capitalize }) =>
        capitalize && {
            textTransform: 'uppercase',
        }}
`;

const BottomContainer = styled(({ active, ...rest }) => <div {...rest} />)`
    position: fixed;
    bottom: 0;
    left: 0;
    height: 50px;
    width: 100%;
    z-index: 30;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    background: #fff;
    transition: ease all 0.2s;
    transform: ${({ active }) => (active ? 'translateY(0)' : 'translateY(100%)')};
    box-shadow: 0px -4px 12px rgba(0, 0, 0, 0.05);
`;

const StyledFab = styled(Fab)`
    && {
        width: 24px;
        height: 24px;
        min-height: auto;
        margin-left: 20px;

        &.MuiFab-root {
            background-color: #fff;
            border: 2px solid ${theme.colors.blue.main};
            color: ${theme.colors.blue.main};

            &:hover {
                background-color: ${theme.colors.blue.main};
                color: #fff;
            }
        }

        &.MuiFab-root.Mui-disabled {
            border-color: rgba(0, 0, 0, 0.26);
            color: rgba(0, 0, 0, 0.26);
        }
    }
`;
const StyledTabsContainer = styled(Tabs)`
    && {
        .MuiTab-root {
            min-width: unset;
            min-height: 10px;
            color: ${theme.colors.blue.main};
            opacity: 1;
        }

        .MuiTabs-indicator {
            border-radius: 100px;
        }

        .Mui-selected {
            color: #fff;
            background-color: ${theme.colors.blue.main};
        }
    }
`;

const StyledTab = styled(Tab)`
    && {
        padding: 8px 15px;
        background-color: #fff;
        border-radius: 20px;
        margin: 0 5px;
    }
`;

const TimetableTabsContainer = styled.div`
    /* && { */
    /* margin-left: 0px;
margin-bottom: 0px; */
    overflow-x: scroll;
    overflow-y: hidden;
    display: flex;
    width: 98%;
    /* max-height: 55px; */
    /* } */
`;

const mapStateToProps = function (state) {
    return {
        schoolID: state.appState.school?.id,
        schoolName: state.appState.school?.name,

        termSchoolID: state.appState.term?.termSchoolId,

        termStartDate: state.appState.term?.startDate,
        termEndDate: state.appState.term?.endDate,

        termCode: state.appState.term?.termCode,
        accountID: state.user?.id,
        firstName: state.user?.firstName,
        lastName: state.user?.lastName,
        accountType: state.user?.accountType,
        termSchools: state.appState.termSchools,
        viewType: state.appState?.viewType,
    };
};

export default connect(mapStateToProps)(Battleship);
